import { use, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import Loader from '../../../components/Loader/Loader';

import { useApiKeyVersions } from '../../../hooks/api';
import useApiErrorsToast from '../../../hooks/api/useApiErrorsToast';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import { EyeCrossedIcon, EyeIcon } from '../../../images/shapes';

interface ApiKeyHistoryModalContentProps {
  /**
   * The ID of the API key for which to display the history.
   */
  id: string;
  /**
   * The name of the API key for which to display the history.
   */
  name: string;
  /**
   * The current API key value, reserved for displaying the active version.
   */
  apiKey: string;
  /**
   * Test ID for the modal content.
   */
  testId?: string;
}

const HIDDEN_VALUE = Array.from({ length: 35 }, () => '*').join('');

export const KeyHistoryModalContent = ({
  id,
  name,
  apiKey,
  testId,
}: ApiKeyHistoryModalContentProps) => {
  const { t } = useTranslation();
  const { resolve } = use(ModalInstanceContext);
  const { data, isLoading, errors } = useApiKeyVersions(id);

  useApiErrorsToast(errors);

  if (isLoading) {
    return (
      <div className="flex min-h-48 items-center justify-center">
        <Loader type="spinner-grid" />
      </div>
    );
  }

  return (
    <div
      className="text-slate-900 dark:text-slate-200 pb-6 sm:pb-0"
      {...getTestProps(testId)}
    >
      <p className="mb-4" {...getTestProps(testId, 'name')}>
        {name}
      </p>

      <div className="relative max-h-100 overflow-y-auto pr-2 -mr-2 scrollbar-sm pb-4 sm:pb-8">
        <div className="flex flex-col gap-2.5">
          <HistoryRow
            action={t('ApiKeys.History.Active')}
            date={t('ApiKeys.History.CurrentlyInUse')}
            value={apiKey}
            isActive
            {...getTestProps(testId, 'active', 'testId')}
          />
          {data.map((version) => (
            <HistoryRow
              key={version.version}
              action={t(`ApiKeys.History.Action.${version.action}`)}
              date={moment(version.createdAt).format('D MMM YYYY, LT')}
              value={version.settings?.apiKey}
              {...getTestProps(testId, `version-${version.version}`, 'testId')}
            />
          ))}
        </div>
      </div>

      <div
        className="w-full absolute left-0 bottom-0 flex items-center justify-between py-6 px-6 lg:px-11 
                    gap-4 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
      >
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('ApiKeys.History.ReadOnly')}
        </p>
        <Button
          color="blue"
          onClick={() => resolve?.(true)}
          {...getTestProps(testId, 'ok-button', 'testId')}
        >
          {t('Global.Ok')}
        </Button>
      </div>
    </div>
  );
};

const HistoryRow = ({
  action,
  date,
  value,
  isActive = false,
  testId,
}: {
  action: string;
  date: string;
  isActive?: boolean;
  value?: string;
  testId?: string;
}) => {
  const [hidden, toggleHidden] = useReducer((state) => !state, true);

  const hasValue = !!value;
  const Component = hasValue ? 'button' : 'div';

  return (
    <Component
      className={twMerge(
        'grid grid-cols-[150px_150px_230px_16px] gap-3 items-start w-full min-w-fit p-4 rounded-md',
        'border border-slate-200 dark:border-slate-700 text-left text-sm bg-white dark:bg-slate-900',
        hasValue && 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800',
      )}
      {...(hasValue && { onClick: toggleHidden, type: 'button' })}
      {...getTestProps(testId, 'toggle-visibility')}
    >
      <div className="flex items-center gap-3 font-semibold">
        <span
          className={twMerge(
            'h-2 w-2 rounded-full',
            isActive
              ? 'bg-lime-300 dark:bg-lime-600'
              : 'bg-slate-300 dark:bg-slate-500',
          )}
        />
        <span {...getTestProps(testId, 'action')}>{action}</span>
      </div>

      <span
        className="text-slate-600 dark:text-slate-400"
        {...getTestProps(testId, 'date')}
      >
        {date}
      </span>

      <HistoryRowValue value={value} hidden={hidden} testId={testId} />
    </Component>
  );
};

const HistoryRowValue = ({
  value,
  hidden,
  testId,
}: {
  value?: string;
  hidden: boolean;
  testId?: string;
}) => {
  if (!value) return 'N/A';
  return (
    <>
      <span {...getTestProps(testId, 'value')}>
        {hidden ? HIDDEN_VALUE : value}
      </span>

      {hidden ? (
        <EyeCrossedIcon className="w-4 text-slate-400 dark:text-slate-200" />
      ) : (
        <EyeIcon className="w-4 text-slate-400 dark:text-slate-200" />
      )}
    </>
  );
};
